import React from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { collectionToStories } from "@quintype/components";
import get from "lodash/get";

import { CollectionName } from "../../Atoms/CollectionName";
import { LoadmoreButton } from "../../Atoms/Loadmore";
import { Subheadline } from "../../Atoms/Subheadline";
import { HeroImage } from "../../Atoms/HeroImage";
import { StoryCard } from "../../Molecules/StoryCard";
import { StorycardContent } from "../../Molecules/StorycardContent";
import { StateProvider } from "../../SharedContext";
import { getTextColor, generateNavigateSlug, navigateTo } from "../../../utils/utils";
import { getCategoryAndSubCategory, useTrackWidgetImpression } from "../../../../ga-utils";
import { getTemplateName } from "../../../../components/utils";

import "./three-col-seven-story.m.css";

const ThreeColSevenStory = ({ collection, config = {} }) => {
  const storyItems = collectionToStories(collection);
  if (storyItems.length < 1) {
    return null;
  }

  const {
    collectionNameBorderColor = "",
    borderColor = "",
    border = "",
    theme = "",
    slotConfig = [],
    collectionNameTemplate = "",
    footerSlotConfig = {},
    footerButton = "",
    customCollectionName,
    navigate = true,
  } = config;
  const { footerSlot } = footerSlotConfig;
  const borderBottomStyle = border === "bottom" ? "card-bottom" : "card";
  const lastCardBorderBottom = slotConfig === "ad" ? "border-unset" : "";
  const dispatch = useDispatch();
  const qtConfig = useSelector((state) => get(state, ["qt", "config"], {}));
  const url = generateNavigateSlug(collection, qtConfig);
  const pageType = useSelector((state) => get(state, ["qt", "pageType"], {}));

  // Track widget impression using the hook
  const isBrowser = typeof window !== "undefined";
  const trackingOptions = {
    sectionName: collection?.name,
    pageType: pageType,
    location: isBrowser ? window.location : {},
    config: qtConfig,
    threshold: 0.2,
  };
  const componentRef = useTrackWidgetImpression(trackingOptions);

  const pushDataLayerEvent = (linkPosition) => {
    if (typeof window !== "undefined" && window.dataLayer) {
      const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType);
      window.dataLayer.push({
        event: "dl_story_listing_component_click",
        link_position: linkPosition,
        section_name: collection?.name?.toLowerCase(),
        category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: getTemplateName(pageType),
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  const handleStoryClick = (position) => {
    pushDataLayerEvent(position);
  };

  // Click handler for Read More button
  const handleReadMoreClick = () => {
    pushDataLayerEvent(-1);
    navigateTo(dispatch, url);
  };

  const handleCollectionNameClick = () => {
    pushDataLayerEvent(-2);
    if (navigate) {
      navigateTo(dispatch, url);
    }
  };

  const storyCardComponent = (story) => {
    return (
      <StoryCard story={story} theme={theme} border={border} isHorizontal config={config}>
        <HeroImage
          story={story}
          isHorizontal
          aspectRatio={[
            [16, 9],
            [16, 9],
          ]}
          showIcon={false}
        />
        <StorycardContent
          theme={theme}
          border={border}
          story={story}
          borderColor={borderColor}
          config={config}
          showAuthorContent={false}
        />
        <Subheadline story={story} />
      </StoryCard>
    );
  };

  const footerSlotComp = footerSlot ? footerSlot() : null;

  const textColor = getTextColor(theme);
  const Cards = border === "bottom" ? "cards" : "";

  return (
    <div
      className="full-width-with-padding arrow-component"
      data-test-id="three-col-seven-stories"
      style={{ backgroundColor: theme, color: textColor }}
      ref={componentRef}
    >
      <div styleName="wrapper">
        <div styleName="collection-name-wrapper">
          <div onClick={handleCollectionNameClick} style={{ cursor: "pointer" }}>
            <CollectionName
              collection={collection}
              collectionNameTemplate={collectionNameTemplate}
              collectionNameBorderColor={collectionNameBorderColor}
              customCollectionName={customCollectionName}
              navigate={navigate}
            />
          </div>
          <div onClick={handleReadMoreClick} style={{ cursor: "pointer" }}>
            <LoadmoreButton
              config={config}
              collection={collection}
              qtConfig={qtConfig}
              isBtnWithArrow
              pageType={pageType}
            />
          </div>
        </div>

        <div styleName="three-col-five-stories">
          <div styleName={`cards card-1 ${Cards}`}>
            {storyItems.slice(0, 1).map((story, index) => {
              return (
                <div
                  styleName={`${borderBottomStyle}`}
                  key={`default-${index}`}
                  onMouseDown={() => handleStoryClick(index + 1)}
                >
                  <StoryCard
                    story={story}
                    theme={theme}
                    aspectRatio={[
                      [16, 9],
                      [16, 9],
                    ]}
                    headerLevel="5"
                    borderColor={borderColor}
                    config={config}
                    showAuthorContent={false}
                  />
                </div>
              );
            })}
          </div>

          {storyItems.length > 1 && (
            <div styleName={`cards card-2 ${Cards}`}>
              {storyItems.slice(1, 3).map((story, index) => {
                return (
                  <div
                    styleName={`${borderBottomStyle}`}
                    key={`default-${index}`}
                    onMouseDown={() => handleStoryClick(index + 2)}
                  >
                    {storyCardComponent(story)}
                  </div>
                );
              })}
            </div>
          )}
          {storyItems.length > 3 && (
            <div styleName="cards card-3">
              {storyItems.slice(3, 6).map((story, index) => {
                return (
                  <div
                    styleName={`${borderBottomStyle} ${lastCardBorderBottom}`}
                    key={`default-${index}`}
                    onMouseDown={() => handleStoryClick(index + 4)}
                  >
                    {storyCardComponent(story)}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <LoadmoreButton
          template={footerButton}
          collection={collection}
          config={config}
          navigate={handleReadMoreClick}
          qtConfig={qtConfig}
          pageType={pageType}
        />
        {footerSlotComp}
      </div>
    </div>
  );
};

ThreeColSevenStory.propTypes = {
  /**  collection is the array of objects which is returning by API  */
  collection: PropTypes.object.isRequired,
  /** The config for the Row */
  config: PropTypes.shape({
    borderColor: PropTypes.string,
    border: PropTypes.string,
    theme: PropTypes.string,
    slotConfig: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    footerButton: PropTypes.string,
    collectionNameTemplate: PropTypes.string,
    customCollectionName: PropTypes.string,
    collectionNameBorderColor: PropTypes.string,
    navigate: PropTypes.bool,
  }),
};

export default StateProvider(ThreeColSevenStory);
